import type { AuthUser } from '@/lib/auth';

type PermissionChecker = (permission: string) => boolean;
type RoleChecker = (roleCode: string) => boolean;

export interface AccessPredicateInput {
  user: AuthUser | null;
  hasPermission: PermissionChecker;
  hasRole: RoleChecker;
}

export interface ActionAccessConfig {
  permissionsAny?: string[];
  permissionsAll?: string[];
  rolesAny?: string[];
  rolesAll?: string[];
}

export type ScopeFieldMode = 'hidden' | 'select' | 'fixed';

export interface RoleScopedFieldRule<TScope = number | string> {
  permissionsAny?: string[];
  permissionsAll?: string[];
  rolesAny?: string[];
  rolesAll?: string[];
  mode: ScopeFieldMode;
  fixedValue?: (user: AuthUser) => TScope | undefined;
}

export interface RoleScopedFieldConfig<TScope = number | string> {
  action?: ActionAccessConfig;
  rules: RoleScopedFieldRule<TScope>[];
  defaultMode?: ScopeFieldMode;
}

export interface RoleScopedFieldDecision<TScope = number | string> {
  allowed: boolean;
  mode: ScopeFieldMode;
  fixedValue?: TScope;
}

function includesAny(values: string[] | undefined, checker: (value: string) => boolean): boolean {
  if (!values || values.length === 0) return true;
  return values.some((value) => checker(value));
}

function includesAll(values: string[] | undefined, checker: (value: string) => boolean): boolean {
  if (!values || values.length === 0) return true;
  return values.every((value) => checker(value));
}

function matchesPredicate(input: AccessPredicateInput, predicate: ActionAccessConfig): boolean {
  return (
    includesAny(predicate.permissionsAny, input.hasPermission) &&
    includesAll(predicate.permissionsAll, input.hasPermission) &&
    includesAny(predicate.rolesAny, input.hasRole) &&
    includesAll(predicate.rolesAll, input.hasRole)
  );
}

export function canAccessAction(
  input: AccessPredicateInput,
  config?: ActionAccessConfig,
): boolean {
  if (!input.user) return false;
  if (!config) return true;
  return matchesPredicate(input, config);
}

export function resolveRoleScopedField<TScope = number | string>(
  input: AccessPredicateInput,
  config: RoleScopedFieldConfig<TScope>,
): RoleScopedFieldDecision<TScope> {
  const actionAllowed = canAccessAction(input, config.action);

  if (!actionAllowed || !input.user) {
    return { allowed: false, mode: 'hidden' };
  }

  for (const rule of config.rules) {
    if (!matchesPredicate(input, rule)) continue;

    if (rule.mode === 'fixed') {
      const fixedValue = rule.fixedValue?.(input.user);
      if (fixedValue === undefined || fixedValue === null) {
        return { allowed: false, mode: 'hidden' };
      }
      return { allowed: true, mode: 'fixed', fixedValue };
    }

    return { allowed: true, mode: rule.mode };
  }

  return { allowed: true, mode: config.defaultMode ?? 'hidden' };
}
